import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchSessionSummary, fetchConcurrencyTimeline, fetchMnoConfigs, checkBackendHealth } from '../services/api';

const TelemetryContext = createContext(null);

export const TelemetryProvider = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLiveBackend, setIsLiveBackend] = useState(false);
  const [forceMock, setForceMock] = useState(false);
  const [mno, setMno] = useState('ALL');
  const [route, setRoute] = useState('ALL');
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [mnoList, setMnoList] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSimulatorDrawerOpen, setIsSimulatorDrawerOpen] = useState(false);

  // Load telemetry data from backend or mock generators
  const loadDashboardData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const isHealthy = await checkBackendHealth();
      setIsLiveBackend(isHealthy);

      const [summaryRes, timelineRes, mnoConfigsRes] = await Promise.all([
        fetchSessionSummary({ mno, route, forceMock }),
        fetchConcurrencyTimeline({ mno, route, forceMock }),
        fetchMnoConfigs(forceMock),
      ]);

      setSummary(summaryRes);
      setTimeline(timelineRes || []);
      if (mnoConfigsRes && mnoConfigsRes.length > 0) {
        setMnoList(mnoConfigsRes);
      }
    } catch (e) {
      console.error('Error fetching dashboard telemetry:', e);
    } finally {
      setIsRefreshing(false);
    }
  }, [mno, route, forceMock]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Real-time polling every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadDashboardData();
    }, 6000);
    return () => clearInterval(interval);
  }, [loadDashboardData]);

  const value = {
    isCollapsed,
    setIsCollapsed,
    isLiveBackend,
    setIsLiveBackend,
    forceMock,
    setForceMock,
    mno,
    setMno,
    route,
    setRoute,
    summary,
    timeline,
    mnoList,
    isRefreshing,
    loadDashboardData,
    isSimulatorDrawerOpen,
    setIsSimulatorDrawerOpen,
  };

  return (
    <TelemetryContext.Provider value={value}>
      {children}
    </TelemetryContext.Provider>
  );
};

export const useTelemetry = () => {
  const context = useContext(TelemetryContext);
  if (!context) {
    throw new Error('useTelemetry must be used within a TelemetryProvider');
  }
  return context;
};

export default TelemetryContext;
